import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import {
	MAT_DIALOG_DATA,
	MatDialog,
	MatDialogModule,
	MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
	MAT_MOMENT_DATE_ADAPTER_OPTIONS,
	MomentDateAdapter,
} from '@angular/material-moment-adapter';
import {
	catchError,
	EMPTY,
	filter,
	finalize,
	forkJoin,
	from,
	map,
	Observable,
	of,
	startWith,
	Subject,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs';
import { FormatUtil } from '@lmc-app/shared/utils/format.util';
import dayjs from 'dayjs';
import { CUSTOM_DATE_FORMAT } from '@lmc-app/shared/constants/common.constant';
import { LmcDialogComponent } from '@lmc-app/shared/components/dialog/lmc-dialog.component';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { LoanMetaService } from '@lmc-app/core/services/loan-meta.service';
import {
	buildPayoffStatementPdf,
	PayableAccount,
	PayoffContext,
	PayoffInfo,
	PayoffQuote,
	PayoffStatementInfo,
	payoffAmount,
} from './payoff-statement';
import { PAYOFF_TYPES } from './promise-to-pay-edit';
import { arrangeOptionFields } from '../arrange/promise-to-pay-arrange-dialog.component';
import {
	pickArrangeBankAccount,
	PromiseToPayBankAccountDialogResult,
} from '../bank-account/promise-to-pay-bank-account-dialog.component';
import {
	ArrangeBankAccount,
	PromiseToPay,
	PromiseToPayFieldError,
	PromiseToPayOwner,
	PromiseToPayService,
} from '@lmc-app/core/services/promise-to-pay.service';
import {
	ARRANGEMENT_TYPE_LABELS,
	ArrangementType,
	NlsTransactionType,
	PAYMENT_METHOD_LABELS,
	PROMISE_TO_PAY_DEPOSIT_EDIT_STATUSES,
	PROMISE_TO_PAY_EDITABLE_STATUSES,
	PromiseToPayPaymentMethod,
	PromiseToPayStatus,
	PromiseToPayType,
} from '@lmc-app/shared/enum/promise-to-pay-status.enum';

export interface PromiseToPayDialogData {
	/** Edit an existing promise, or omit to create one. */
	promise?: PromiseToPay;
	/** Create only: the loan the promise is for. */
	acctrefno?: number;
	loanNumber?: string;
	customerName?: string;
	/** Create only: picked from the panel's Create menu. */
	type?: PromiseToPayType;
	/** Loan-page values for the payoff quote; without it there is no quote. */
	payoff?: PayoffContext;
	/** Edit only: owner candidates, passed when the user may change owner. */
	owners?: PromiseToPayOwner[];
	/** Backdate permission: may pick a past promise date. */
	canBackdate?: boolean;
	/** Edit permission: payment method and status stay editable in any status. */
	canEdit?: boolean;
}

/** The saved promise as the server returns it, or undefined when cancelled. */
export type PromiseToPayDialogResult = PromiseToPay | undefined;

interface PromiseToPayFormModel {
	type: PromiseToPayType | null;
	arrangementType: ArrangementType | null;
	paymentMethod: PromiseToPayPaymentMethod | null;
	amount: number | null;
	promiseDate: Date | null;
	comments: string | null;
	ownerId: number | null;
	status: PromiseToPayStatus | null;
	/** Only while arranging; see isArranging. */
	transactionType?: NlsTransactionType;
	payoff?: boolean;
}

/**
 * Creates or edits a promise to pay — a PTP or an arrangement, told apart by `type`.
 *
 * Every rule that used to live in the browser (weekday only, no past date, no duplicate,
 * the open-promise cap) is now enforced by the API; this form only shapes the request and
 * renders back whatever the server rejected. That is deliberate: the legacy screen's
 * client-side checks were bypassable and had drifted from what the data actually allowed.
 *
 * A Pay Off arrangement quotes its amount from NLS through the legacy payoff endpoints
 * when a date is picked, and shows the statement with a PDF download.
 */
@Component({
	selector: 'app-promise-to-pay-dialog',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		FormlyModule,
		FormlyMaterialModule,
		FormlyMatDatepickerModule,
		MatDialogModule,
		MatButtonModule,
		MatIconModule,
		MatProgressSpinnerModule,
		MatTooltipModule,
	],
	providers: [
		{
			provide: DateAdapter,
			useClass: MomentDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
		},
		{ provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMAT },
	],
	templateUrl: './promise-to-pay-dialog.component.html',
	styleUrls: ['./promise-to-pay-dialog.component.scss'],
})
export class PromiseToPayDialogComponent implements OnInit, OnDestroy {
	readonly form = new FormGroup({});
	model: PromiseToPayFormModel;
	fields: FormlyFieldConfig[] = [];

	readonly isEdit: boolean;
	readonly loanNumber: string;
	readonly customerName: string;

	isSaving = false;
	isQuoting = false;
	quoteError: string | null = null;
	readonly FormatUtil = FormatUtil;
	/** Last payoff quote; only shown while the date and arrangement type still match it. */
	private quote: PayoffQuote | null = null;
	private quoteArrangementType: ArrangementType | null = null;

	private readonly destroy$ = new Subject<void>();

	constructor(
		private readonly dialogRef: MatDialogRef<
			PromiseToPayDialogComponent,
			PromiseToPayDialogResult
		>,
		private readonly promiseToPayService: PromiseToPayService,
		private readonly dialog: MatDialog,
		private readonly legacyApi: LegacyApiService,
		private readonly loanMetaService: LoanMetaService,
		private readonly dateAdapter: DateAdapter<unknown>,
		@Inject(MAT_DIALOG_DATA) public readonly data: PromiseToPayDialogData
	) {
		this.isEdit = !!data.promise;
		this.loanNumber = data.promise?.loanNumber ?? data.loanNumber ?? '';
		this.customerName = data.promise?.customerName ?? data.customerName ?? '';

		this.model = {
			type: data.promise?.type ?? data.type ?? PromiseToPayType.PTP,
			arrangementType: data.promise?.arrangementType ?? null,
			paymentMethod: data.promise?.paymentMethod ?? null,
			amount: data.promise?.amount ?? null,
			// The datepicker holds Moments; reads go through dayjs, which accepts both.
			promiseDate: data.promise?.promiseDate
				? (this.dateAdapter.deserialize(data.promise.promiseDate) as Date)
				: null,
			comments: data.promise?.comments ?? null,
			ownerId: this.currentOwnerId(),
			status: data.promise?.status ?? null,
		};
	}

	private currentOwnerId(): number | null {
		return this.data.promise?.ownerId ?? null;
	}

	/** Candidates, plus the current owner when they are not one, so the select shows them. */
	private ownerOptions(): { value: number; label: string }[] {
		const options = (this.data.owners ?? []).map((owner) => ({
			value: owner.employeeID,
			label: owner.fullName,
		}));
		const ownerId = this.currentOwnerId();
		if (ownerId != null && !options.some((o) => o.value === ownerId)) {
			options.unshift({
				value: ownerId,
				label: this.data.promise?.ownerName ?? `Employee ${ownerId}`,
			});
		}
		return options;
	}

	/** The statuses a customer deposit, or any promise with Edit, may be set to. */
	private statusOptions(): { value: PromiseToPayStatus; label: string }[] {
		return PROMISE_TO_PAY_DEPOSIT_EDIT_STATUSES.map((value) => ({
			value,
			label: value,
		}));
	}

	private get statusChange(): PromiseToPayStatus | null {
		const status = this.model.status;
		const isDeposit =
			this.model.paymentMethod === PromiseToPayPaymentMethod.CustomerDeposit;
		const canSetStatus = isDeposit || !!this.data.canEdit;
		return canSetStatus && status && status !== this.data.promise?.status
			? status
			: null;
	}

	/** A manual debit with no transaction set to Arranged raises its KapCharge payment, as Arrange does. */
	private isArranging(
		model: Partial<PromiseToPayFormModel> | undefined
	): boolean {
		return (
			!!this.data.promise &&
			!this.data.promise.transactionId &&
			this.data.promise.status !== PromiseToPayStatus.Arranged &&
			model?.status === PromiseToPayStatus.Arranged &&
			model?.paymentMethod === PromiseToPayPaymentMethod.ManualDebit
		);
	}

	ngOnInit(): void {
		this.fields = this.buildFields();
		this.watchPayoffInputs();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	get title(): string {
		const noun =
			this.model.type === PromiseToPayType.Arrangement
				? 'Arrangement'
				: 'Promise to Pay';
		return `${this.isEdit ? 'Edit' : 'New'} ${noun}`;
	}

	/**
	 * Type comes from the caller and is not a field. Arrangement type is fixed once a
	 * promise exists: changing it would make it a different record.
	 */
	private buildFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroupClassName: 'row',
				fieldGroup: [
					{
						className: 'col-6',
						key: 'arrangementType',
						type: 'select',
						props: {
							label: 'Arrangement Type',
							required: true,
							appearance: 'outline',
							disabled: this.isEdit,
							options: Object.values(ArrangementType).map((value) => ({
								value,
								label: ARRANGEMENT_TYPE_LABELS[value],
							})),
						},
						expressions: {
							hide: (field) =>
								field.model?.type !== PromiseToPayType.Arrangement,
						},
					},
					{
						className: 'col-6',
						key: 'paymentMethod',
						type: 'select',
						props: {
							label: 'Payment Method',
							required: true,
							appearance: 'outline',
							// Past Requested, without Edit, only a customer deposit is editable, and it stays one.
							disabled:
								this.isEdit &&
								!this.data.canEdit &&
								!PROMISE_TO_PAY_EDITABLE_STATUSES.includes(
									this.data.promise!.status
								),
							options: Object.values(PromiseToPayPaymentMethod).map(
								(value) => ({ value, label: PAYMENT_METHOD_LABELS[value] })
							),
						},
					},
					{
						className: 'col-6',
						key: 'promiseDate',
						type: 'datepicker',
						props: {
							label: 'Promise Date',
							required: true,
							appearance: 'outline',
						},
						expressions: {
							// Backdate may pick a past date for any promise.
							'props.datepickerOptions.min': () =>
								this.canBackdate ? null : dayjs().startOf('day').toDate(),
						},
					},
					{
						className: 'col-6',
						key: 'amount',
						type: 'currency',
						props: {
							label: 'Amount',
							required: true,
						},
						validators: {
							positive: {
								expression: (c: { value: unknown }) =>
									Number(String(c.value ?? '').replace(/,/g, '')) > 0,
								message: () => 'Amount must be greater than zero',
							},
						},
					},
					{
						className: 'col-6',
						key: 'ownerId',
						type: 'select',
						// Edit only, for users granted Change Owner in Admin Settings.
						hide: !this.isEdit || !this.data.owners?.length,
						props: {
							label: 'Owner',
							required: true,
							appearance: 'outline',
							options: this.ownerOptions(),
						},
					},
					{
						className: 'col-6',
						key: 'status',
						type: 'select',
						// Restores the current status if the field is hidden and shown again.
						defaultValue: this.data.promise?.status,
						props: {
							label: 'Status',
							required: true,
							appearance: 'outline',
							options: this.statusOptions(),
						},
						expressions: {
							// Edit only, for customer deposits, or any promise with Edit.
							hide: (field) =>
								!this.isEdit ||
								(!this.data.canEdit &&
									field.model?.paymentMethod !==
										PromiseToPayPaymentMethod.CustomerDeposit),
							'props.description': (field) => {
								if (this.isArranging(field.model)) {
									return 'Saving raises the KapCharge transaction and NLS payment.';
								}
								return field.model?.paymentMethod ===
									PromiseToPayPaymentMethod.ManualDebit
									? 'Warning: changing a manual debit status does not update its KapCharge transaction or NLS payment.'
									: '';
							},
						},
					},
					{
						className: 'col-6',
						fieldGroup: arrangeOptionFields(),
						expressions: {
							hide: (field) => !this.isArranging(field.model),
						},
					},
					{
						className: 'col-12',
						key: 'comments',
						type: 'textarea',
						// Not taken on create; an existing comment is shown read-only on edit.
						hide: !this.data.promise?.comments,
						props: {
							label: 'Comments',
							appearance: 'outline',
							disabled: true,
							rows: 3,
							maxLength: 400,
						},
					},
				],
			},
		];
	}

	/** Pay Off arrangements quote the payoff as soon as a date is picked. */
	get isPayoff(): boolean {
		return (
			!!this.data.payoff &&
			this.model.type === PromiseToPayType.Arrangement &&
			PAYOFF_TYPES.includes(this.model.arrangementType)
		);
	}

	private get canBackdate(): boolean {
		return !!this.data.canBackdate;
	}

	/** The quote, if it was made for the current date and arrangement type. */
	get currentQuote(): PayoffQuote | null {
		if (!this.quote || !this.model.promiseDate) return null;
		const sameDate =
			this.quote.payoffDate ===
			dayjs(this.model.promiseDate).format('YYYY-MM-DD');
		const sameType = this.quoteArrangementType === this.model.arrangementType;
		return sameDate && sameType ? this.quote : null;
	}

	/**
	 * Re-quotes whenever the date or arrangement type changes. Edit also quotes on open, but
	 * keeps the saved amount until the user changes the date or type.
	 */
	private watchPayoffInputs(): void {
		let lastKey = this.payoffKey();
		const changes$ = this.form.valueChanges.pipe(
			map(() => this.payoffKey()),
			filter((key) => key !== lastKey),
			tap((key) => (lastKey = key)),
			map(() => true)
		);
		const fillAmount$ =
			this.isEdit && lastKey ? changes$.pipe(startWith(false)) : changes$;
		fillAmount$
			.pipe(
				switchMap((fillAmount) => this.requestPayoff(fillAmount)),
				takeUntil(this.destroy$)
			)
			.subscribe();
	}

	private payoffKey(): string | null {
		if (!this.isPayoff || !this.model.promiseDate) return null;
		return `${this.model.arrangementType}|${dayjs(
			this.model.promiseDate
		).format('YYYY-MM-DD')}`;
	}

	/**
	 * Quotes the payoff from NLS for the promise date and fills in the amount, the same
	 * way the legacy arrangement form did.
	 */
	private requestPayoff(fillAmount: boolean): Observable<unknown> {
		this.quoteError = null;
		if (!this.isPayoff || !this.model.promiseDate) return EMPTY;

		const payoff = this.data.payoff!;
		const date = dayjs(this.model.promiseDate);
		if (!this.canBackdate && date.isBefore(dayjs(), 'day')) {
			this.quoteError = 'Not a valid Payoff Date';
			return EMPTY;
		}

		const payoffDate = date.format('YYYY-MM-DD');
		const arrangementType = this.model.arrangementType;
		const includeNsf = arrangementType === ArrangementType.PayOff;
		const acctrefno = this.data.promise?.acctrefno ?? this.data.acctrefno;

		this.isQuoting = true;
		return forkJoin({
			statement: this.legacyApi
				.getPayoffStatment(this.loanNumber)
				.pipe(map((rows) => (rows as PayoffStatementInfo[])[0])),
			info: this.legacyApi
				.getPayoffInfo(acctrefno, payoffDate)
				.pipe(map((res) => res as PayoffInfo)),
			payable: from(
				this.loanMetaService.getPayableBankingAccount(payoff.loanGroupNo!)
			).pipe(
				map((res) => (res as unknown as { data: PayableAccount }).data),
				// The statement still renders without the bank details.
				catchError(() => of(null))
			),
		}).pipe(
			tap(({ statement, info, payable }) => {
				if (!statement || !info) {
					this.quoteError = 'Could not get the payoff from NLS.';
					return;
				}
				const amount = payoffAmount(info, includeNsf, payoff.nsfFees);
				this.quote = {
					payoffDate,
					includeNsf,
					info,
					statement,
					payable,
					insurance: payoff.insurance,
					nsfFees: payoff.nsfFees,
					originationDate: payoff.originationDate,
					amount,
				};
				this.quoteArrangementType = arrangementType;
				if (fillAmount) this.form.get('amount')?.setValue(amount as never);
			}),
			catchError(() => {
				this.quoteError = 'Could not get the payoff from NLS.';
				return EMPTY;
			}),
			finalize(() => (this.isQuoting = false))
		);
	}

	onDownloadPayoff(): void {
		const quote = this.currentQuote;
		if (!quote) return;
		buildPayoffStatementPdf(quote, this.customerName).save(
			this.customerName + ' Payoff Statement.pdf'
		);
	}

	onSave(): void {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const amount = Number(String(this.model.amount ?? '').replace(/,/g, ''));
		const promiseDate = dayjs(this.model.promiseDate!).format('YYYY-MM-DD');
		const isArranging = this.isArranging(this.model);

		// Arranging a loan with no active bank account first picks one of its other accounts.
		const bankAccount$: Observable<PromiseToPayBankAccountDialogResult> =
			isArranging
				? pickArrangeBankAccount(this.dialog, {
						...this.data.promise!,
						amount,
						promiseDate,
				  })
				: of(null);

		bankAccount$
			.pipe(
				filter((bankAccount) => bankAccount !== undefined),
				tap(() => (this.isSaving = true)),
				switchMap((bankAccount) =>
					this.saveRequest(
						amount,
						promiseDate,
						isArranging,
						bankAccount ?? null
					)
				),
				takeUntil(this.destroy$)
			)
			.subscribe({
				next: (saved) => {
					this.isSaving = false;
					this.dialogRef.close(saved);
				},
				error: (err) => {
					this.isSaving = false;
					this.applyServerErrors(err);
				},
			});
	}

	private saveRequest(
		amount: number,
		promiseDate: string,
		isArranging: boolean,
		bankAccount: ArrangeBankAccount | null
	): Observable<PromiseToPay> {
		return this.isEdit
			? this.promiseToPayService.update(this.data.promise!.id, {
					paymentMethod: this.model.paymentMethod!,
					amount,
					promiseDate,
					comments: this.model.comments,
					...(this.model.ownerId !== this.currentOwnerId()
						? { ownerId: this.model.ownerId! }
						: {}),
					...(this.statusChange ? { status: this.statusChange } : {}),
					...(isArranging
						? {
								transactionType: this.model.transactionType,
								payoff:
									this.model.transactionType === NlsTransactionType.Payment
										? !!this.model.payoff
										: false,
								...(bankAccount ? { bankAccount } : {}),
						  }
						: {}),
			  })
			: this.promiseToPayService.create({
					acctrefno: this.data.acctrefno!,
					type: this.model.type!,
					arrangementType:
						this.model.type === PromiseToPayType.Arrangement
							? this.model.arrangementType
							: null,
					paymentMethod: this.model.paymentMethod!,
					amount,
					promiseDate,
					comments: this.model.comments,
			  });
	}

	/**
	 * Highlights the controls a server rejection named — red border only, no inline text,
	 * since a long server message (e.g. the open-promise cap's two-line explanation) reads
	 * better as one alert than wrapped under a single field — and shows the actual wording
	 * in an LmcDialogComponent alert. An unmatched field name still surfaces its message the
	 * same way a matched one does.
	 */
	private applyServerErrors(err: unknown): void {
		const body = (err as { error?: unknown })?.error as
			| { errors?: PromiseToPayFieldError[]; message?: string }
			| undefined;

		const fieldErrors = body?.errors;
		let message: string;
		if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
			fieldErrors.forEach(({ field }) => {
				const control = this.form.get(field);
				if (control) {
					// Empty string still marks the control invalid (any key does); there is
					// nothing to resolve to text since the message goes in the alert instead.
					control.setErrors({ server: '' });
					control.markAsTouched();
				}
			});
			message = fieldErrors.map((e) => e.message).join(' ');
		} else {
			message = body?.message ?? 'Failed to save. Please try again.';
		}

		this.dialog.open(LmcDialogComponent, {
			data: {
				title: 'Could Not Save',
				content: message,
				positiveButtonText: 'OK',
			},
		});
	}

	onCancel(): void {
		this.dialogRef.close();
	}
}
